import React, { useState } from 'react';
import {
    styled,
    Box,
    Typography,
    CardActions,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { ActionButton } from 'components/ui/buttons';
import { downloadFile } from 'utility/browser';
import { useNotification } from 'context/notification';
import { useAuth } from 'context/auth';
import fetchExpresso from 'utility/fetchExpresso';
import GetAppIcon from '@mui/icons-material/GetApp';
import moment from 'moment-timezone';

export default function TicketDownloader(props) {
    const { multiTicket, tranId, eventId, lastName, email, onDownload } = props;

    const [delivery, setDelivery] = useState('pdf'); // pdf, zip, qr-only
    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();
    const { auth } = useAuth();

    const handleDownload = () => {
        setLoading(true);

        fetchExpresso(
            `/apiv2/events/${eventId}/orders/${tranId}/tickets?delivery=${delivery}&tz=${moment.tz.guess()}`,
        )
            .then(async (res) => {
                if (res.status === 200) {
                    const pdf = await res.text();
                    await downloadFile(`tickets-${tranId}-${lastName}`, pdf);

                    // Create a log on successful downloads
                    fetchExpresso(`/apiv2/ticket_activity/`, {
                        method: 'POST',
                        body: { tranId, typeId: 200, email },
                    })
                        .then(() => {
                            if (onDownload) {
                                onDownload();
                            }
                        })
                        .catch(() => null);

                    setLoading(false);
                } else {
                    throw new Error();
                }
            })
            .catch(() => {
                notify.error('Unable to download tickets');
                setLoading(false);
            });
    };

    return (
        <Card>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center">
                        <GetAppIcon />
                        <Title>Download Tickets</Title>
                    </Box>
                }
                subheader="Download this order's tickets as PDF files."
            />
            <CardContent>
                <Box minHeight={multiTicket ? '160px' : '60px'}>
                    {multiTicket && (
                        <Box mt={4}>
                            <Box mb={2}>
                                <Typography style={{ fontWeight: 'bold' }}>
                                    How should the tickets be downloaded?
                                </Typography>
                            </Box>
                            <RadioGroup
                                aria-label="bundle"
                                value={delivery}
                                onChange={(e) => setDelivery(e.target.value)}
                            >
                                <FormControlLabel
                                    value="pdf"
                                    control={<Radio />}
                                    label={'Combine all tickets into one PDF'}
                                />
                                <Box mt={2} />
                                <FormControlLabel
                                    value="zip"
                                    control={<Radio />}
                                    label={
                                        <>
                                            <Typography>
                                                Download each ticket as a separate PDF
                                            </Typography>
                                            <Typography variant="caption">
                                                Best when sharing tickets with multiple people.
                                            </Typography>
                                        </>
                                    }
                                />
                                {auth.admin && (
                                    <>
                                        <Box mt={2} />
                                        <FormControlLabel
                                            value="qr-only"
                                            control={<Radio />}
                                            label={
                                                <>
                                                    <Typography>
                                                        QR Codes Only (Admin)
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        Useful if you design or print your own tickets
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </>
                                )}
                            </RadioGroup>
                        </Box>
                    )}
                </Box>
            </CardContent>
            <CardActions>
                <ActionButton
                    disabled={loading}
                    onClick={handleDownload}
                    startIcon={<GetAppIcon />}
                >
                    {loading ? 'Downloading...' : 'Download Tickets'}
                </ActionButton>
            </CardActions>
        </Card>
    );
}

const Title = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
    display: 'inline',
}));
Title.defaultProps = { variant: 'h6' };
